import React, { useState, useEffect, useMemo } from "react";
import { makeStyles } from "@material-ui/core/styles";
import Modal from "react-modal";
import moment from "moment";
import "moment/locale/th";

// Material-UI & Components
import Grid from "components/Grid/GridContainer.js";
import Col from "components/Grid/GridItem.js";
import Card from "components/Card/Card.js";
import CardHeader from "components/Card/CardHeader.js";
import CardBody from "components/Card/CardBody.js";
import Button from "components/CustomButtons/Button.js";
import TSUser from "components/Forms/TSUser";

// API Service
import { KpiService } from "_services/kpi.service";

const styles = {
  cardTitleWhite: {
    color: "#FFFFFF",
    marginTop: "0px",
    minHeight: "auto",
    fontWeight: "400",
    fontFamily: '"Sarabun", sans-serif',
    marginBottom: "3px"
  },
  modalOverlay: {
    backgroundColor: "rgba(0,0,0,0.6)",
    zIndex: 1050
  },
  modalContent: {
    top: "50%",
    left: "50%",
    right: "auto",
    bottom: "auto",
    marginRight: "-50%",
    transform: "translate(-50%, -50%)",
    width: "96vw",
    maxWidth: "1600px",
    height: "94vh",
    maxHeight: "94vh",
    overflowY: "auto",
    padding: "25px 35px",
    borderRadius: "10px",
    boxShadow: "0 10px 40px rgba(0,0,0,0.35)",
    fontFamily: '"Sarabun", sans-serif'
  },
  assignModalContent: {
    top: "50%",
    left: "50%",
    right: "auto",
    bottom: "auto",
    marginRight: "-50%",
    transform: "translate(-50%, -50%)",
    width: "1100px",
    maxWidth: "96vw",
    maxHeight: "94vh",
    overflowY: "auto",
    padding: "25px",
    borderRadius: "8px",
    fontFamily: '"Sarabun", sans-serif'
  },
  criteriaCard: {
    borderRadius: "6px",
    padding: "10px 14px",
    marginBottom: "8px",
    borderLeft: "5px solid"
  }
};
const useStyles = makeStyles(styles);

const KpiManagement = () => {
  const classes = useStyles();
  moment.locale("th");

  // --- States ---
  const [periods, setPeriods] = useState([]);
  const [selectedPeriodId, setSelectedPeriodId] = useState("");
  const [selectedPeriod, setSelectedPeriod] = useState(null);
  const [kpiItems, setKpiItems] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Review & Hierarchy States
  const [statusFilter, setStatusFilter] = useState("ALL"); // ALL, PENDING, APPROVED, REJECTED
  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);
  const [reviewingItem, setReviewingItem] = useState(null);
  const [reviewAction, setReviewAction] = useState("REJECT"); // APPROVE, REJECT
  const [reviewRemark, setReviewRemark] = useState("");

  // Modals
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  const [editingItemId, setEditingItemId] = useState(null);
  const [viewingItem, setViewingItem] = useState(null);
  const [assigningItem, setAssigningItem] = useState(null);

  // Assign Users Form State
  const [assignedUsersList, setAssignedUsersList] = useState([]);

  // --- Plan/Project Options & Management ---
  const DEFAULT_PLAN_PROJECTS = ["คำรับรอง กพร.", "งานประจำ", "งานพัฒนาคุณภาพ"];

  const [customProjects, setCustomProjects] = useState(() => {
    try {
      const saved = localStorage.getItem("kpi_managed_plan_projects");
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  const [isProjectModalOpen, setIsProjectModalOpen] = useState(false);
  const [newProjectName, setNewProjectName] = useState("");

  // Form State (รองรับครบทั้ง 15 ฟิลด์ตามความต้องการ + Reviewer & Department)
  const initialFormState = {
    PeriodID: "",
    KpiType: 1, // 1: COMMON (ตัวชี้วัดส่วนกลาง), 2: INDIVIDUAL (ตัวชี้วัดระดับหน่วยงาน)
    Code: "",
    KpiName: "",
    PlanProjectName: "",
    StrategicAlignment: "",
    DepartmentKey: null,
    ReviewerUserName: "",
    TargetScoreLevel: 3, // Dropdown ระดับ 1-5
    WeightType: 1, // 1: น้ำหนักบังคับ (Fixed), 2: น้ำหนักที่ปรับเองได้ (Flexible)
    DefaultWeight: 10.0,
    UnitName: "ร้อยละ",
    Description: "",
    CriteriaLevel1: "",
    CriteriaLevel2: "",
    CriteriaLevel3: "",
    CriteriaLevel4: "",
    CriteriaLevel5: "",
    Criteria12M_Level1: "",
    Criteria12M_Level2: "",
    Criteria12M_Level3: "",
    Criteria12M_Level4: "",
    Criteria12M_Level5: "",
    CalculationFormula: "",
    ScoreExample: "",
    BaselineData: ""
  };

  const [formData, setFormData] = useState(initialFormState);

  // Combine Default, Custom (from localStorage), and any project names in kpiItems
  const allPlanProjects = useMemo(() => {
    const list = [...DEFAULT_PLAN_PROJECTS];
    if (Array.isArray(customProjects)) {
      customProjects.forEach(p => {
        if (p && !list.includes(p)) list.push(p);
      });
    }
    if (Array.isArray(kpiItems)) {
      kpiItems.forEach(item => {
        if (item.planProjectName && !list.includes(item.planProjectName)) {
          list.push(item.planProjectName);
        }
      });
    }
    if (formData.PlanProjectName && !list.includes(formData.PlanProjectName)) {
      list.push(formData.PlanProjectName);
    }
    return list;
  }, [customProjects, kpiItems, formData.PlanProjectName]);

  const handleAddCustomProject = e => {
    if (e) e.preventDefault();
    const trimmed = newProjectName.trim();
    if (!trimmed) return;
    if (allPlanProjects.includes(trimmed)) {
      alert("มีแผนงาน / โครงการนี้อยู่ในรายการแล้ว");
      return;
    }
    const updated = [...customProjects, trimmed];
    setCustomProjects(updated);
    try {
      localStorage.setItem(
        "kpi_managed_plan_projects",
        JSON.stringify(updated)
      );
    } catch (err) {
      console.error(err);
    }
    setFormData(prev => ({ ...prev, PlanProjectName: trimmed }));
    setNewProjectName("");
  };

  const handleDeleteCustomProject = projectToDelete => {
    if (DEFAULT_PLAN_PROJECTS.includes(projectToDelete)) {
      alert("ไม่สามารถลบตัวเลือกหลักของระบบได้");
      return;
    }
    if (window.confirm(`ต้องการลบตัวเลือก "${projectToDelete}" หรือไม่?`)) {
      const updated = customProjects.filter(p => p !== projectToDelete);
      setCustomProjects(updated);
      try {
        localStorage.setItem(
          "kpi_managed_plan_projects",
          JSON.stringify(updated)
        );
      } catch (err) {
        console.error(err);
      }
      if (formData.PlanProjectName === projectToDelete) {
        setFormData(prev => ({ ...prev, PlanProjectName: "" }));
      }
    }
  };

  useEffect(() => {
    Modal.setAppElement("body");
    fetchPeriods();
  }, []);

  useEffect(() => {
    if (selectedPeriodId) {
      const found = periods.find(p => p.periodID === selectedPeriodId);
      setSelectedPeriod(found || null);
      fetchKpiItems(selectedPeriodId);
    } else {
      setKpiItems([]);
      setSelectedPeriod(null);
    }
  }, [selectedPeriodId, periods]);

  const fetchPeriods = async () => {
    try {
      const response = await KpiService.getAllPeriods();
      if (response && response.data && response.data.data) {
        const list = response.data.data;
        setPeriods(list);
        if (list.length > 0) {
          const currentTarget = KpiService.findCurrentPeriod(list);
          setSelectedPeriodId(
            currentTarget ? currentTarget.periodID : list[0].periodID
          );
        }
      }
    } catch (error) {
      console.error("Error fetching periods:", error);
    }
  };

  const fetchKpiItems = async periodId => {
    setIsLoading(true);
    try {
      const response = await KpiService.getItemsByPeriod(periodId);
      if (response && response.data && response.data.data) {
        setKpiItems(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching KPI items:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = e => {
    const { name, value } = e.target;
    let val = value;
    if (
      name === "KpiType" ||
      name === "TargetScoreLevel" ||
      name === "WeightType"
    ) {
      val = parseInt(value, 10);
    } else if (name === "DefaultWeight") {
      val = parseFloat(value) || 0;
    }
    setFormData(prev => ({
      ...prev,
      [name]: val
    }));
  };

  const handleOpenCreateModal = () => {
    setEditingItemId(null);
    setFormData({
      ...initialFormState,
      PeriodID: selectedPeriodId,
      Code: `KPI-${kpiItems.length + 1}`
    });
    setIsFormModalOpen(true);
  };

  const handleOpenEditModal = item => {
    setEditingItemId(item.kpiItemID);
    setFormData({
      PeriodID: item.periodID,
      KpiType: item.kpiType,
      Code: item.code || "",
      KpiName: item.kpiName || "",
      PlanProjectName: item.planProjectName || "",
      StrategicAlignment: item.strategicAlignment || "",
      DepartmentKey: item.departmentKey || null,
      ReviewerUserName: item.reviewerUserName || "",
      TargetScoreLevel: item.targetScoreLevel || 3,
      WeightType: item.weightType || 1,
      DefaultWeight: item.defaultWeight || 0,
      UnitName: item.unitName || "ร้อยละ",
      Description: item.description || "",
      CriteriaLevel1: item.criteriaLevel1 || "",
      CriteriaLevel2: item.criteriaLevel2 || "",
      CriteriaLevel3: item.criteriaLevel3 || "",
      CriteriaLevel4: item.criteriaLevel4 || "",
      CriteriaLevel5: item.criteriaLevel5 || "",
      Criteria12M_Level1: item.criteria12M_Level1 || "",
      Criteria12M_Level2: item.criteria12M_Level2 || "",
      Criteria12M_Level3: item.criteria12M_Level3 || "",
      Criteria12M_Level4: item.criteria12M_Level4 || "",
      Criteria12M_Level5: item.criteria12M_Level5 || "",
      CalculationFormula: item.calculationFormula || "",
      ScoreExample: item.scoreExample || "",
      BaselineData: item.baselineData || ""
    });
    setIsFormModalOpen(true);
  };

  const handleCopyCriteria = direction => {
    if (direction === "6to12") {
      setFormData(prev => ({
        ...prev,
        Criteria12M_Level1: prev.CriteriaLevel1,
        Criteria12M_Level2: prev.CriteriaLevel2,
        Criteria12M_Level3: prev.CriteriaLevel3,
        Criteria12M_Level4: prev.CriteriaLevel4,
        Criteria12M_Level5: prev.CriteriaLevel5
      }));
    } else if (direction === "12to6") {
      setFormData(prev => ({
        ...prev,
        CriteriaLevel1: prev.Criteria12M_Level1,
        CriteriaLevel2: prev.Criteria12M_Level2,
        CriteriaLevel3: prev.Criteria12M_Level3,
        CriteriaLevel4: prev.Criteria12M_Level4,
        CriteriaLevel5: prev.Criteria12M_Level5
      }));
    }
  };

  const handleOpenDetailModal = item => {
    setViewingItem(item);
    setIsDetailModalOpen(true);
  };

  const handleOpenAssignModal = item => {
    setAssigningItem(item);
    const existingUsers = (
      item.assignedUsers ||
      item.userAssignments ||
      []
    ).map(a => ({
      userName: a.userName,
      firstName: a.firstName || a.fullName || a.userName,
      lastName: a.lastName || "",
      nickName: a.nickName || "",
      fullName:
        a.fullName ||
        `${a.firstName || ""} ${a.lastName || ""}`.trim() ||
        a.userName,
      position: a.positionName || "",
      department: a.departmentName || "",
      weight:
        item.weightType === 1
          ? item.defaultWeight || 0
          : a.weight !== undefined
          ? Math.min(a.weight, item.defaultWeight || 0)
          : item.defaultWeight || 0
    }));
    setAssignedUsersList(existingUsers);
    setIsAssignModalOpen(true);
  };

  const handleUserChange = selectedUsers => {
    const isFixed = assigningItem?.weightType === 1;
    const defaultWt = assigningItem?.defaultWeight || 0;

    const updated = (selectedUsers || []).map(u => {
      const existing = assignedUsersList.find(
        x => x.userName.toLowerCase() === (u.userName || "").toLowerCase()
      );
      return {
        userName: u.userName,
        firstName: u.firstName || "",
        lastName: u.lastName || "",
        nickName: u.nickName || "",
        fullName:
          `${u.firstName || ""} ${u.lastName || ""}${
            u.nickName ? ` (${u.nickName})` : ""
          }`.trim() || u.userName,
        position: u.position || "",
        department: u.department?.departmentName || u.department || "",
        weight: isFixed
          ? defaultWt
          : existing?.weight !== undefined
          ? Math.min(parseFloat(existing.weight) || 0, defaultWt)
          : defaultWt
      };
    });
    setAssignedUsersList(updated);
  };

  const handleUserWeightChange = (userName, newWeight) => {
    if (assigningItem?.weightType === 1) return; // น้ำหนักบังคับ ไม่สามารถปรับรายบุคคลได้
    const val = newWeight === "" ? "" : parseFloat(newWeight);
    setAssignedUsersList(prev =>
      prev.map(u =>
        u.userName.toLowerCase() === userName.toLowerCase()
          ? { ...u, weight: isNaN(val) ? "" : val }
          : u
      )
    );
  };

  const handleRemoveUserFromAssign = userName => {
    setAssignedUsersList(
      assignedUsersList.filter(
        u => u.userName.toLowerCase() !== userName.toLowerCase()
      )
    );
  };

  const handleSaveAssignments = async () => {
    if (!assigningItem) return;
    const isFixed = assigningItem.weightType === 1;
    const defaultWt = assigningItem.defaultWeight || 0;

    if (!isFixed) {
      const exceededUsers = assignedUsersList.filter(
        u =>
          (parseFloat(u.weight) || 0) > defaultWt ||
          (parseFloat(u.weight) || 0) < 0
      );
      if (exceededUsers.length > 0) {
        alert(
          `ไม่สามารถบันทึกได้ เนื่องจากค่าน้ำหนักต้องไม่เกินค่าน้ำหนักสูงสุด (${defaultWt}%):\n` +
            exceededUsers
              .map(u => `- ${u.fullName || u.userName}: ${u.weight}%`)
              .join("\n")
        );
        return;
      }
    }

    setIsSaving(true);
    try {
      await KpiService.assignUsers(assigningItem.kpiItemID, {
        UserAssignments: assignedUsersList.map(u => ({
          UserName: u.userName,
          Weight: isFixed ? defaultWt : (parseFloat(u.weight) || defaultWt)
        })),
        DefaultWeight: defaultWt
      });
      alert("บันทึกการมอบหมายตัวชี้วัดสำเร็จ!");
      setIsAssignModalOpen(false);
      fetchKpiItems(selectedPeriodId);
    } catch (error) {
      alert(
        "เกิดข้อผิดพลาด: " +
          (error.response?.data?.errors?.[0]?.message || error.message)
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveKpiItem = async e => {
    e.preventDefault();
    if (!formData.PeriodID) {
      alert("กรุณาเลือกรอบการประเมิน");
      return;
    }
    if (!formData.KpiName.trim()) {
      alert("กรุณากรอกชื่อ KPI");
      return;
    }

    setIsSaving(true);
    try {
      if (editingItemId) {
        await KpiService.updateItem(editingItemId, formData);
        alert("แก้ไขตัวชี้วัดสำเร็จ!");
      } else {
        await KpiService.createItem(formData);
        alert("สร้างตัวชี้วัดใหม่สำเร็จ!");
      }
      setIsFormModalOpen(false);
      fetchKpiItems(selectedPeriodId);
    } catch (error) {
      alert(
        "เกิดข้อผิดพลาด: " +
          (error.response?.data?.errors?.[0]?.message || error.message)
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteKpiItem = async itemId => {
    if (window.confirm("คุณแน่ใจหรือไม่ว่าต้องการลบตัวชี้วัดนี้?")) {
      try {
        await KpiService.deleteItem(itemId);
        alert("ลบตัวชี้วัดสำเร็จ!");
        fetchKpiItems(selectedPeriodId);
      } catch (error) {
        alert("เกิดข้อผิดพลาดในการลบ: " + error.message);
      }
    }
  };

  const handleQuickApprove = async item => {
    if (
      window.confirm(`ยืนยันการอนุมัติตัวชี้วัด "${item.kpiName}" ใช่หรือไม่?`)
    ) {
      setIsSaving(true);
      try {
        await KpiService.reviewItem(item.kpiItemID, {
          status: 2, // Approved
          reviewRemark: "อนุมัติแล้ว"
        });
        alert("อนุมัติตัวชี้วัดเรียบร้อยแล้ว");
        fetchKpiItems(selectedPeriodId);
      } catch (error) {
        alert(
          "เกิดข้อผิดพลาด: " +
            (error.response?.data?.errors?.[0]?.message || error.message)
        );
      } finally {
        setIsSaving(false);
      }
    }
  };

  const handleOpenRejectModal = item => {
    setReviewingItem(item);
    setReviewAction("REJECT");
    setReviewRemark("");
    setIsReviewModalOpen(true);
  };

  const handleSubmitReview = async () => {
    if (!reviewingItem) return;
    if (reviewAction === "REJECT" && !reviewRemark.trim()) {
      alert("กรุณาระบุเหตุผลหรือข้อเสนอแนะในการส่งกลับแก้ไข");
      return;
    }

    setIsSaving(true);
    try {
      await KpiService.reviewItem(reviewingItem.kpiItemID, {
        status: reviewAction === "APPROVE" ? 2 : 3,
        reviewRemark: reviewRemark.trim()
      });
      alert(
        reviewAction === "APPROVE"
          ? "อนุมัติตัวชี้วัดเรียบร้อยแล้ว"
          : "ส่งกลับแก้ไขตัวชี้วัดเรียบร้อยแล้ว"
      );
      setIsReviewModalOpen(false);
      fetchKpiItems(selectedPeriodId);
    } catch (error) {
      alert(
        "เกิดข้อผิดพลาด: " +
          (error.response?.data?.errors?.[0]?.message || error.message)
      );
    } finally {
      setIsSaving(false);
    }
  };

  // Filtered Items
  const filteredKpiItems = useMemo(() => {
    if (statusFilter === "PENDING") {
      return kpiItems.filter(i => i.status === 1 || i.status === 0);
    }
    if (statusFilter === "APPROVED") {
      return kpiItems.filter(i => i.status === 2);
    }
    if (statusFilter === "REJECTED") {
      return kpiItems.filter(i => i.status === 3);
    }
    return kpiItems;
  }, [kpiItems, statusFilter]);

  const pendingCount = kpiItems.filter(i => i.status === 1 || i.status === 0)
    .length;
  const approvedCount = kpiItems.filter(i => i.status === 2).length;
  const rejectedCount = kpiItems.filter(i => i.status === 3).length;

  // Helper summary
  const totalWeight = kpiItems.reduce(
    (sum, item) => sum + (item.defaultWeight || 0),
    0
  );
  const commonCount = kpiItems.filter(i => i.kpiType === 1).length;
  const individualCount = kpiItems.filter(i => i.kpiType === 2).length;

  return (
    <div>
      {/* --- Filter & Action Bar --- */}
      <Grid>
        <Col xs={12} sm={12} md={12}>
          <Card>
            <CardHeader
              color="info"
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "wrap"
              }}
            >
              <div>
                <h4
                  className={classes.cardTitleWhite}
                  style={{ margin: 0, fontWeight: "bold" }}
                >
                  <i className="fas fa-tasks mr-2"></i>{" "}
                  จัดการตัวชี้วัดผลการปฏิบัติราชการ (KPI Management)
                </h4>
                <p
                  style={{
                    color: "rgba(255,255,255,0.85)",
                    margin: "4px 0 0 0",
                    fontSize: "14px"
                  }}
                >
                  สร้างและกำหนดตัวชี้วัด (KPI ส่วนกลาง / ส่วนบุคคล)
                  พร้อมเกณฑ์ประเมิน 5 ระดับ และสูตรคำนวณ
                </p>
              </div>

              <div
                style={{
                  display: "flex",
                  gap: "10px",
                  alignItems: "center",
                  marginTop: "8px"
                }}
              >
                {/* เลือกรอบการประเมิน Dropdown */}
                <div style={{ minWidth: "260px" }}>
                  <select
                    className="form-control"
                    value={selectedPeriodId}
                    onChange={e => setSelectedPeriodId(e.target.value)}
                    style={{
                      height: "38px",
                      fontWeight: "bold",
                      backgroundColor: "#ffffff",
                      color: "#333"
                    }}
                  >
                    {periods.length === 0 && (
                      <option value="">-- ไม่พบรอบการประเมิน --</option>
                    )}
                    {periods.map(p => (
                      <option key={p.periodID} value={p.periodID}>
                        {p.periodName} ({p.fiscalYear})
                      </option>
                    ))}
                  </select>
                </div>

                <Button
                  color="success"
                  size="sm"
                  onClick={handleOpenCreateModal}
                  disabled={!selectedPeriodId}
                >
                  <i className="fas fa-plus-circle mr-1"></i> สร้าง KPI ใหม่
                </Button>
              </div>
            </CardHeader>

            <CardBody>
              {/* สรุปรอบและน้ำหนักคะแนน Banner */}
              {selectedPeriod && (
                <div
                  className="p-3 mb-3 rounded"
                  style={{
                    backgroundColor: "#f4f8fb",
                    border: "1px solid #d9e6f2",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    flexWrap: "wrap"
                  }}
                >
                  <div>
                    <span
                      className="font-weight-bold text-dark"
                      style={{ fontSize: "15px" }}
                    >
                      <i className="fas fa-bullseye text-primary mr-2"></i>
                      {selectedPeriod.periodName}
                    </span>
                    <span className="ml-2 badge badge-pill badge-info">
                      {selectedPeriod.kpiRound === 1
                        ? "รอบ 6 เดือน"
                        : "รอบ 12 เดือน"}
                    </span>
                    <span className="ml-2 badge badge-pill badge-secondary">
                      {selectedPeriod.currentTimelineName}
                    </span>
                  </div>

                  <div style={{ fontSize: "13px" }}>
                    <span className="mr-3">
                      ตัวชี้วัดส่วนกลาง:{" "}
                      <strong className="text-primary">{commonCount}</strong>{" "}
                      ข้อ
                    </span>
                    <span className="mr-3">
                      ตัวชี้วัดระดับหน่วยงาน:{" "}
                      <strong className="text-warning">
                        {individualCount}
                      </strong>{" "}
                      ข้อ
                    </span>
                    <span>
                      น้ำหนักรวมทั้งหมด:{" "}
                      <strong
                        className={`badge ${
                          totalWeight === 100
                            ? "badge-success"
                            : totalWeight > 100
                            ? "badge-danger"
                            : "badge-warning"
                        }`}
                        style={{ fontSize: "13px", padding: "4px 8px" }}
                      >
                        {totalWeight.toFixed(2)} %
                      </strong>
                    </span>
                  </div>
                </div>
              )}

              {/* แท็บกรองสถานะการอนุมัติ (Filter Status Tabs) */}
              <div
                className="d-flex mb-3"
                style={{ gap: "8px", flexWrap: "wrap" }}
              >
                <button
                  type="button"
                  className={`btn btn-sm ${
                    statusFilter === "ALL"
                      ? "btn-info font-weight-bold"
                      : "btn-outline-secondary"
                  }`}
                  onClick={() => setStatusFilter("ALL")}
                  style={{ borderRadius: "20px", padding: "4px 14px" }}
                >
                  ทั้งหมด ({kpiItems.length})
                </button>
                <button
                  type="button"
                  className={`btn btn-sm ${
                    statusFilter === "PENDING"
                      ? "btn-warning text-dark font-weight-bold shadow-sm"
                      : "btn-outline-warning text-dark"
                  }`}
                  onClick={() => setStatusFilter("PENDING")}
                  style={{ borderRadius: "20px", padding: "4px 14px" }}
                >
                  <i className="fas fa-clock mr-1"></i> รอพิจารณา/รออนุมัติ (
                  {pendingCount})
                </button>
                <button
                  type="button"
                  className={`btn btn-sm ${
                    statusFilter === "APPROVED"
                      ? "btn-success font-weight-bold shadow-sm"
                      : "btn-outline-success"
                  }`}
                  onClick={() => setStatusFilter("APPROVED")}
                  style={{ borderRadius: "20px", padding: "4px 14px" }}
                >
                  <i className="fas fa-check-circle mr-1"></i> อนุมัติแล้ว (
                  {approvedCount})
                </button>
                <button
                  type="button"
                  className={`btn btn-sm ${
                    statusFilter === "REJECTED"
                      ? "btn-danger font-weight-bold shadow-sm"
                      : "btn-outline-danger"
                  }`}
                  onClick={() => setStatusFilter("REJECTED")}
                  style={{ borderRadius: "20px", padding: "4px 14px" }}
                >
                  <i className="fas fa-undo mr-1"></i> ส่งกลับแก้ไข (
                  {rejectedCount})
                </button>
              </div>

              {/* Table รายการตัวชี้วัด */}
              {isLoading ? (
                <div className="text-center p-4">
                  <i className="fas fa-spinner fa-spin fa-2x text-info"></i>
                  <p className="mt-2">กำลังโหลดรายการตัวชี้วัด...</p>
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-bordered table-hover mt-1">
                    <thead className="thead-light">
                      <tr
                        style={{
                          backgroundColor: "#f8f9fa",
                          textAlign: "center"
                        }}
                      >
                        <th style={{ width: "40px" }}>#</th>
                        <th style={{ width: "135px" }}>ประเภท</th>
                        <th style={{ width: "130px" }}>รหัส / สังกัด</th>
                        <th>ชื่อตัวชี้วัดผลงาน (KPI)</th>
                        <th style={{ width: "85px" }}>เป้าหมาย</th>
                        <th style={{ width: "110px" }}>น้ำหนัก</th>
                        <th style={{ width: "140px" }}>สถานะ</th>
                        <th style={{ width: "100px" }}>ผู้รับมอบหมาย</th>
                        <th style={{ width: "165px" }}>จัดการ / อนุมัติ</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredKpiItems.length === 0 ? (
                        <tr>
                          <td
                            colSpan="9"
                            className="text-center py-4 text-muted"
                          >
                            <i className="fas fa-info-circle mr-1"></i>{" "}
                            {statusFilter === "ALL"
                              ? "ยังไม่มีตัวชี้วัดในรอบนี้ กรุณากดปุ่ม + สร้าง KPI ใหม่"
                              : "ไม่พบรายการตัวชี้วัดในสถานะที่เลือก"}
                          </td>
                        </tr>
                      ) : (
                        filteredKpiItems.map((item, index) => (
                          <tr key={item.kpiItemID}>
                            <td className="text-center align-middle">
                              {index + 1}
                            </td>
                            <td className="text-center align-middle">
                              {item.kpiType === 1 ? (
                                <span
                                  className="badge badge-primary px-2 py-1"
                                  style={{ fontSize: "11px" }}
                                >
                                  <i className="fas fa-users mr-1"></i>{" "}
                                  ตัวชี้วัดส่วนกลาง
                                </span>
                              ) : (
                                <span
                                  className="badge badge-warning px-2 py-1 text-dark"
                                  style={{ fontSize: "11px" }}
                                >
                                  <i className="fas fa-building mr-1"></i>{" "}
                                  ตัวชี้วัดระดับหน่วยงาน
                                </span>
                              )}
                            </td>
                            <td
                              className="align-middle"
                              style={{ fontSize: "12px" }}
                            >
                              <div className="font-weight-bold text-dark">
                                {item.code || "-"}
                              </div>
                              {item.departmentName && (
                                <div
                                  className="text-muted"
                                  style={{ fontSize: "11px" }}
                                >
                                  <i className="fas fa-sitemap mr-1"></i>
                                  {item.departmentName}
                                </div>
                              )}
                              {item.createdBy && (
                                <div
                                  className="text-secondary"
                                  style={{ fontSize: "10px" }}
                                >
                                  สร้างโดย: {item.createdBy}
                                </div>
                              )}
                            </td>
                            <td className="align-middle">
                              <strong>{item.kpiName}</strong>
                              {item.planProjectName && (
                                <div
                                  className="text-muted"
                                  style={{ fontSize: "12px" }}
                                >
                                  โครงการ: {item.planProjectName}
                                </div>
                              )}
                              {item.strategicAlignment && (
                                <div
                                  className="text-muted"
                                  style={{ fontSize: "12px" }}
                                >
                                  ยุทธศาสตร์: {item.strategicAlignment}
                                </div>
                              )}
                            </td>
                            <td className="text-center align-middle">
                              <span className="badge badge-info px-2 py-1">
                                ระดับ {item.targetScoreLevel}
                              </span>
                            </td>
                            <td className="text-center align-middle">
                              <div
                                className="font-weight-bold text-primary"
                                style={{ fontSize: "13px" }}
                              >
                                {item.defaultWeight}%
                              </div>
                              <div style={{ fontSize: "10px" }}>
                                {item.weightType === 1 ? (
                                  <span
                                    className="text-danger font-weight-bold"
                                    title="น้ำหนักบังคับ (เปลี่ยนไม่ได้ ใช้เหมือนกันทุกคน)"
                                  >
                                    <i className="fas fa-lock mr-1"></i>บังคับ (คงที่)
                                  </span>
                                ) : (
                                  <span
                                    className="text-success font-weight-bold"
                                    title={`น้ำหนักที่ปรับได้ (ค่าน้ำหนักสูงสุด ${item.defaultWeight}% ห้ามเกินนี้)`}
                                  >
                                    <i className="fas fa-sliders-h mr-1"></i>
                                    ปรับได้ (สูงสุด {item.defaultWeight}%)
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className="text-center align-middle">
                              {item.status === 2 ? (
                                <div>
                                  <span
                                    className="badge badge-success px-2 py-1"
                                    style={{ fontSize: "11px" }}
                                  >
                                    <i className="fas fa-check-circle mr-1"></i>{" "}
                                    อนุมัติแล้ว
                                  </span>
                                  {item.approvedFullName && (
                                    <div
                                      className="text-muted mt-1"
                                      style={{ fontSize: "10px" }}
                                    >
                                      โดย: {item.approvedFullName}
                                    </div>
                                  )}
                                </div>
                              ) : item.status === 3 ? (
                                <div>
                                  <span
                                    className="badge badge-danger px-2 py-1"
                                    style={{ fontSize: "11px" }}
                                  >
                                    <i className="fas fa-undo mr-1"></i>{" "}
                                    ส่งกลับแก้ไข
                                  </span>
                                  {item.reviewRemark && (
                                    <div
                                      className="text-danger mt-1 font-italic text-truncate"
                                      style={{
                                        fontSize: "10px",
                                        maxWidth: "140px"
                                      }}
                                      title={item.reviewRemark}
                                    >
                                      {item.reviewRemark}
                                    </div>
                                  )}
                                </div>
                              ) : (
                                <div>
                                  <span
                                    className="badge badge-warning text-dark px-2 py-1"
                                    style={{ fontSize: "11px" }}
                                  >
                                    <i className="fas fa-clock mr-1"></i>{" "}
                                    รอพิจารณา
                                  </span>
                                  {item.reviewerFullName && (
                                    <div
                                      className="text-muted mt-1"
                                      style={{ fontSize: "10px" }}
                                    >
                                      ตรวจ: {item.reviewerFullName}
                                    </div>
                                  )}
                                </div>
                              )}
                            </td>
                            <td className="text-center align-middle">
                              <button
                                className="btn btn-sm btn-outline-primary"
                                style={{
                                  padding: "2px 8px",
                                  fontSize: "12px",
                                  borderRadius: "15px"
                                }}
                                title="มอบหมายผู้ใช้"
                                onClick={() => handleOpenAssignModal(item)}
                              >
                                <i className="fas fa-user-plus mr-1"></i>
                                {item.assignedUsers
                                  ? item.assignedUsers.length
                                  : (item.userAssignments &&
                                      item.userAssignments.length) ||
                                    0}{" "}
                                คน
                              </button>
                            </td>
                            <td className="text-center align-middle">
                              {/* ดูรายละเอียด */}
                              <button
                                className="btn btn-sm btn-outline-secondary mr-1"
                                title="ดูเกณฑ์ 5 ระดับและสูตร"
                                onClick={() => handleOpenDetailModal(item)}
                              >
                                <i className="fas fa-eye"></i>
                              </button>

                              {/* ปุ่มอนุมัติ (สำหรับผู้มีสิทธิ์ CanApprove) */}
                              {item.canApprove && item.status !== 2 && (
                                <button
                                  className="btn btn-sm btn-success mr-1"
                                  title="อนุมัติตัวชี้วัด"
                                  onClick={() => handleQuickApprove(item)}
                                >
                                  <i className="fas fa-check"></i>
                                </button>
                              )}

                              {/* ปุ่มส่งกลับแก้ไข (สำหรับผู้มีสิทธิ์ CanApprove) */}
                              {item.canApprove && (
                                <button
                                  className="btn btn-sm btn-outline-danger mr-1"
                                  title="ส่งกลับแก้ไขตัวชี้วัด"
                                  onClick={() => handleOpenRejectModal(item)}
                                >
                                  <i className="fas fa-undo"></i>
                                </button>
                              )}

                              {/* ปุ่มแก้ไข (ขึ้นอยู่กับ CanEdit) */}
                              {/* KpiType = 1 อนุมัติแล้ว แก้ไขอะไรไม่ได้ เพิ่มลด คนรับผิดชอบ ได้อย่างเดียว */}
                              {item.kpiType === 1 && item.status === 2 ? (
                                <button
                                  className="btn btn-sm btn-outline-secondary mr-1"
                                  disabled
                                  title="ตัวชี้วัดส่วนกลาง (Common KPI) ได้รับการอนุมัติแล้ว เพิ่มลดผู้รับผิดชอบได้อย่างเดียว"
                                >
                                  <i className="fas fa-lock"></i>
                                </button>
                              ) : item.canEdit ? (
                                <button
                                  className="btn btn-sm btn-outline-info mr-1"
                                  title="แก้ไข KPI"
                                  onClick={() => handleOpenEditModal(item)}
                                >
                                  <i className="fas fa-edit"></i>
                                </button>
                              ) : (
                                <button
                                  className="btn btn-sm btn-outline-secondary mr-1"
                                  disabled
                                  title="อยู่นอกช่วงเวลาจัดทำ หรือได้รับการอนุมัติแล้ว หรือไม่มีสิทธิ์แก้ไข"
                                >
                                  <i className="fas fa-lock"></i>
                                </button>
                              )}

                              {/* ปุ่มลบ */}
                              {item.canEdit && !(item.kpiType === 1 && item.status === 2) && (
                                <button
                                  className="btn btn-sm btn-outline-danger"
                                  title="ลบ KPI"
                                  onClick={() =>
                                    handleDeleteKpiItem(item.kpiItemID)
                                  }
                                >
                                  <i className="fas fa-trash-alt"></i>
                                </button>
                              )}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </CardBody>
          </Card>
        </Col>
      </Grid>

      {/* ========================================================================= */}
      {/* --- Modal สร้าง / แก้ไขตัวชี้วัด (Create/Edit KPI Item) --- */}
      {/* ========================================================================= */}
      <Modal
        isOpen={isFormModalOpen}
        onRequestClose={() => setIsFormModalOpen(false)}
        style={{
          overlay: styles.modalOverlay,
          content: styles.modalContent
        }}
        contentLabel="ฟอร์มสร้างและแก้ไขตัวชี้วัด"
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            borderBottom: "2px solid #007bff",
            paddingBottom: "10px"
          }}
        >
          <h4 style={{ margin: 0, color: "#007bff", fontWeight: "bold" }}>
            <i className="fas fa-edit mr-2"></i>
            {editingItemId
              ? "แก้ไขตัวชี้วัด (Edit KPI)"
              : "สร้างตัวชี้วัดใหม่ (New KPI)"}
          </h4>
          <button
            type="button"
            className="close"
            onClick={() => setIsFormModalOpen(false)}
          >
            <span>&times;</span>
          </button>
        </div>

        <form onSubmit={handleSaveKpiItem} className="mt-3">
          {/* ข้อมูลทั่วไป */}
          <div className="form-row">
            <div className="form-group col-md-4">
              <label className="font-weight-bold">
                ประเภทตัวชี้วัด <span className="text-danger">*</span>
              </label>
              <select
                name="KpiType"
                className="form-control"
                value={formData.KpiType}
                onChange={handleInputChange}
                required
              >
                <option value={1}>ตัวชี้วัดส่วนกลาง (Central KPI)</option>
                <option value={2}>
                  ตัวชี้วัดระดับหน่วยงาน (Department KPI)
                </option>
              </select>
            </div>

            <div className="form-group col-md-4">
              <label className="font-weight-bold">รหัสตัวชี้วัด (Code)</label>
              <input
                type="text"
                name="Code"
                className="form-control"
                placeholder="เช่น KPI-1 หรือ KPI-COMMON-01"
                value={formData.Code}
                onChange={handleInputChange}
              />
            </div>

            <div className="form-group col-md-4">
              <label className="font-weight-bold">หน่วยวัด (Unit)</label>
              <input
                type="text"
                name="UnitName"
                className="form-control"
                placeholder="เช่น ร้อยละ, ครั้ง, คน, วัน"
                value={formData.UnitName}
                onChange={handleInputChange}
              />
            </div>
          </div>

          <div className="form-group">
            <label className="font-weight-bold">
              ชื่อตัวชี้วัดผลการปฏิบัติงาน (KPI Name){" "}
              <span className="text-danger">*</span>
            </label>
            <textarea
              name="KpiName"
              rows={2}
              className="form-control"
              placeholder="ระบุชื่อตัวชี้วัด เช่น ร้อยละความสำเร็จในการจัดทำรายงาน..."
              value={formData.KpiName}
              onChange={handleInputChange}
              required
            />
          </div>

          <div className="form-row">
            <div className="form-group col-md-6">
              <div className="d-flex justify-content-between align-items-center mb-1">
                <label className="font-weight-bold mb-0">
                  แผนงาน / โครงการ (Plan/Project)
                </label>
                <button
                  type="button"
                  className="btn btn-xs btn-outline-primary py-0 px-2"
                  style={{
                    fontSize: "11px",
                    borderRadius: "12px",
                    border: "1px solid #007bff"
                  }}
                  onClick={() => setIsProjectModalOpen(true)}
                  title="จัดการตัวเลือกแผนงาน/โครงการ"
                >
                  <i className="fas fa-cog mr-1"></i> จัดการตัวเลือก
                </button>
              </div>
              <select
                name="PlanProjectName"
                className="form-control"
                value={formData.PlanProjectName}
                onChange={e => {
                  if (e.target.value === "__MANAGE__") {
                    setIsProjectModalOpen(true);
                  } else {
                    handleInputChange(e);
                  }
                }}
              >
                <option value="">-- เลือกแผนงาน / โครงการ --</option>
                <optgroup label="ตัวเลือกหลัก">
                  {DEFAULT_PLAN_PROJECTS.map(proj => (
                    <option key={proj} value={proj}>
                      {proj}
                    </option>
                  ))}
                </optgroup>
                {allPlanProjects.filter(p => !DEFAULT_PLAN_PROJECTS.includes(p))
                  .length > 0 && (
                  <optgroup label="ตัวเลือกเพิ่มเติม / ที่เคยบันทึก">
                    {allPlanProjects
                      .filter(p => !DEFAULT_PLAN_PROJECTS.includes(p))
                      .map(proj => (
                        <option key={proj} value={proj}>
                          {proj}
                        </option>
                      ))}
                  </optgroup>
                )}
                <option
                  value="__MANAGE__"
                  style={{ color: "#007bff", fontWeight: "bold" }}
                >
                  ➕ เพิ่ม / จัดการตัวเลือกโครงการ...
                </option>
              </select>
            </div>

            <div className="form-group col-md-6">
              <label className="font-weight-bold">
                ความสอดคล้องเชิงยุทธศาสตร์ (Alignment)
              </label>
              <select
                name="StrategicAlignment"
                className="form-control font-weight-bold"
                value={formData.StrategicAlignment}
                onChange={handleInputChange}
              >
                <option value="">-- กรุณาเลือก --</option>
                <option value="ยุทธศาสตร์">ยุทธศาสตร์</option>
                <option value="ภารกิจ">ภารกิจ</option>
              </select>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group col-md-4">
              <label className="font-weight-bold">
                ระดับค่าเป้าหมาย (Target Score Level){" "}
                <span className="text-danger">*</span>
              </label>
              <select
                name="TargetScoreLevel"
                className="form-control font-weight-bold text-primary"
                value={formData.TargetScoreLevel}
                onChange={handleInputChange}
                required
              >
                <option value={1}>ระดับ 1</option>
                <option value={2}>ระดับ 2</option>
                <option value={3}>ระดับ 3 (เป้าหมายมาตรฐาน)</option>
                <option value={4}>ระดับ 4</option>
                <option value={5}>ระดับ 5 (ดีเยี่ยม)</option>
              </select>
            </div>

            <div className="form-group col-md-4">
              <label className="font-weight-bold">
                ประเภทน้ำหนัก (Weight Type)
              </label>
              <select
                name="WeightType"
                className="form-control"
                value={formData.WeightType}
                onChange={handleInputChange}
              >
                <option value={1}>
                  น้ำหนักบังคับ (Fixed Weight) - เปลี่ยนไม่ได้ ใช้เหมือนกัน
                </option>
                <option value={2}>
                  น้ำหนักที่ปรับได้ (Flexible Weight) - กำหนดค่าน้ำหนักสูงสุด
                </option>
              </select>
            </div>

            <div className="form-group col-md-4">
              <label className="font-weight-bold">
                {parseInt(formData.WeightType, 10) === 2 ? (
                  <>
                    ค่าน้ำหนักสูงสุด (% Max Weight){" "}
                    <span className="text-danger">*</span>
                  </>
                ) : (
                  <>
                    ค่าน้ำหนัก (% Weight){" "}
                    <span className="text-danger">*</span>
                  </>
                )}
              </label>
              <div className="input-group">
                <input
                  type="number"
                  step="0.1"
                  min="0"
                  max="100"
                  name="DefaultWeight"
                  className="form-control font-weight-bold text-success"
                  value={formData.DefaultWeight}
                  onChange={handleInputChange}
                  placeholder={
                    parseInt(formData.WeightType, 10) === 2
                      ? "ระบุค่าน้ำหนักสูงสุด"
                      : "ระบุค่าน้ำหนัก"
                  }
                  required
                />
                <div className="input-group-append">
                  <span className="input-group-text">%</span>
                </div>
              </div>
              <small className="form-text">
                {parseInt(formData.WeightType, 10) === 2 ? (
                  <span className="text-info font-weight-bold">
                    <i className="fas fa-info-circle mr-1"></i>
                    ค่าน้ำหนักสูงสุดที่ใช้ได้ ห้ามเกินนั้น (ปรับรายบุคคลได้แต่ห้ามเกินค่านี้)
                  </span>
                ) : (
                  <span className="text-muted">
                    <i className="fas fa-lock mr-1"></i>
                    ค่าน้ำหนักเปลี่ยนไม่ได้ ทุกคนใช้เหมือนกัน
                  </span>
                )}
              </small>
            </div>
          </div>

          <div className="form-group">
            <label className="font-weight-bold">
              คำอธิบายตัวชี้วัด (Description)
            </label>
            <textarea
              name="Description"
              rows={2}
              className="form-control"
              placeholder="อธิบายรายละเอียด คำนิยาม หรือความสำคัญของตัวชี้วัด"
              value={formData.Description}
              onChange={handleInputChange}
            />
          </div>

          {/* เกณฑ์การให้คะแนน 5 ระดับ (แยก 6 เดือน vs 12 เดือน) */}
          <div className="d-flex flex-wrap justify-content-between align-items-center border-bottom pb-2 mt-4 mb-3">
            <div>
              <h5 className="font-weight-bold text-primary mb-1">
                <i className="fas fa-list-ol mr-2"></i> เกณฑ์การให้คะแนน 5 ระดับ
                (Evaluation Criteria)
              </h5>
              <small className="text-muted">
                กำหนดเกณฑ์คะแนนแยกตามรอบประเมิน (รอบ 6 เดือน vs รอบ 12 เดือน)
                ได้ในตัวชี้วัดเดียวกัน
              </small>
            </div>
            <div className="btn-group mt-2 mt-md-0">
              <button
                type="button"
                className="btn btn-sm btn-outline-primary"
                onClick={() => handleCopyCriteria("6to12")}
                title="คัดลอกเกณฑ์จากรอบ 6 เดือนไปใส่รอบ 12 เดือน"
              >
                <i className="fas fa-arrow-right mr-1"></i> คัดลอก 6 เดือน ➔ 12
                เดือน
              </button>
              <button
                type="button"
                className="btn btn-sm btn-outline-success"
                onClick={() => handleCopyCriteria("12to6")}
                title="คัดลอกเกณฑ์จากรอบ 12 เดือนไปใส่รอบ 6 เดือน"
              >
                <i className="fas fa-arrow-left mr-1"></i> คัดลอก 12 เดือน ➔ 6
                เดือน
              </button>
            </div>
          </div>

          <div className="row">
            {/* คอลัมน์ซ้าย: รอบ 6 เดือน */}
            <div className="col-12 col-xl-6 mb-3">
              <div
                className="card h-100 border-primary"
                style={{ borderWidth: "2px" }}
              >
                <div className="card-header bg-primary text-white py-2 d-flex justify-content-between align-items-center">
                  <span className="font-weight-bold">
                    <i className="far fa-calendar-alt mr-1"></i> เกณฑ์คะแนนรอบ 6
                    เดือน
                  </span>
                  <span className="badge badge-light text-primary">
                    รอบที่ 1
                  </span>
                </div>
                <div className="card-body p-3">
                  <div className="form-group mb-2">
                    <label className="font-weight-bold text-danger small mb-1">
                      ระดับ 1 (ผลงานขั้นต่ำ):
                    </label>
                    <textarea
                      name="CriteriaLevel1"
                      rows={2}
                      className="form-control"
                      placeholder="ระบุผลงานหรือเงื่อนไขที่ได้รับคะแนนระดับ 1 (รอบ 6 เดือน)"
                      value={formData.CriteriaLevel1}
                      onChange={handleInputChange}
                    />
                  </div>

                  <div className="form-group mb-2">
                    <label className="font-weight-bold text-warning small mb-1">
                      ระดับ 2:
                    </label>
                    <textarea
                      name="CriteriaLevel2"
                      rows={2}
                      className="form-control"
                      placeholder="ระบุผลงานหรือเงื่อนไขที่ได้รับคะแนนระดับ 2 (รอบ 6 เดือน)"
                      value={formData.CriteriaLevel2}
                      onChange={handleInputChange}
                    />
                  </div>

                  <div className="form-group mb-2">
                    <label className="font-weight-bold text-primary small mb-1">
                      ระดับ 3 (ค่าเป้าหมายมาตรฐาน):{" "}
                      <span className="text-danger">*</span>
                    </label>
                    <textarea
                      name="CriteriaLevel3"
                      rows={2}
                      className="form-control"
                      placeholder="ระบุผลงานหรือเงื่อนไขที่ได้รับคะแนนระดับ 3 (เป้าหมาย 6 เดือน)"
                      value={formData.CriteriaLevel3}
                      onChange={handleInputChange}
                    />
                  </div>

                  <div className="form-group mb-2">
                    <label className="font-weight-bold text-info small mb-1">
                      ระดับ 4:
                    </label>
                    <textarea
                      name="CriteriaLevel4"
                      rows={2}
                      className="form-control"
                      placeholder="ระบุผลงานหรือเงื่อนไขที่ได้รับคะแนนระดับ 4 (รอบ 6 เดือน)"
                      value={formData.CriteriaLevel4}
                      onChange={handleInputChange}
                    />
                  </div>

                  <div className="form-group mb-0">
                    <label className="font-weight-bold text-success small mb-1">
                      ระดับ 5 (ดีเยี่ยม/เกินเป้าหมาย):
                    </label>
                    <textarea
                      name="CriteriaLevel5"
                      rows={2}
                      className="form-control"
                      placeholder="ระบุผลงานหรือเงื่อนไขที่ได้รับคะแนนระดับ 5 (รอบ 6 เดือน)"
                      value={formData.CriteriaLevel5}
                      onChange={handleInputChange}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* คอลัมน์ขวา: รอบ 12 เดือน */}
            <div className="col-12 col-xl-6 mb-3">
              <div
                className="card h-100 border-success"
                style={{ borderWidth: "2px" }}
              >
                <div className="card-header bg-success text-white py-2 d-flex justify-content-between align-items-center">
                  <span className="font-weight-bold">
                    <i className="far fa-calendar-check mr-1"></i> เกณฑ์คะแนนรอบ
                    12 เดือน (สิ้นปี)
                  </span>
                  <span className="badge badge-light text-success">
                    รอบที่ 2
                  </span>
                </div>
                <div className="card-body p-3">
                  <div className="form-group mb-2">
                    <label className="font-weight-bold text-danger small mb-1">
                      ระดับ 1 (ผลงานขั้นต่ำ):
                    </label>
                    <textarea
                      name="Criteria12M_Level1"
                      rows={2}
                      className="form-control"
                      placeholder="ระบุผลงานหรือเงื่อนไขที่ได้รับคะแนนระดับ 1 (รอบ 12 เดือน)"
                      value={formData.Criteria12M_Level1}
                      onChange={handleInputChange}
                    />
                  </div>

                  <div className="form-group mb-2">
                    <label className="font-weight-bold text-warning small mb-1">
                      ระดับ 2:
                    </label>
                    <textarea
                      name="Criteria12M_Level2"
                      rows={2}
                      className="form-control"
                      placeholder="ระบุผลงานหรือเงื่อนไขที่ได้รับคะแนนระดับ 2 (รอบ 12 เดือน)"
                      value={formData.Criteria12M_Level2}
                      onChange={handleInputChange}
                    />
                  </div>

                  <div className="form-group mb-2">
                    <label className="font-weight-bold text-primary small mb-1">
                      ระดับ 3 (ค่าเป้าหมายมาตรฐาน):
                    </label>
                    <textarea
                      name="Criteria12M_Level3"
                      rows={2}
                      className="form-control"
                      placeholder="ระบุผลงานหรือเงื่อนไขที่ได้รับคะแนนระดับ 3 (เป้าหมาย 12 เดือน)"
                      value={formData.Criteria12M_Level3}
                      onChange={handleInputChange}
                    />
                  </div>

                  <div className="form-group mb-2">
                    <label className="font-weight-bold text-info small mb-1">
                      ระดับ 4:
                    </label>
                    <textarea
                      name="Criteria12M_Level4"
                      rows={2}
                      className="form-control"
                      placeholder="ระบุผลงานหรือเงื่อนไขที่ได้รับคะแนนระดับ 4 (รอบ 12 เดือน)"
                      value={formData.Criteria12M_Level4}
                      onChange={handleInputChange}
                    />
                  </div>

                  <div className="form-group mb-0">
                    <label className="font-weight-bold text-success small mb-1">
                      ระดับ 5 (ดีเยี่ยม/เกินเป้าหมาย):
                    </label>
                    <textarea
                      name="Criteria12M_Level5"
                      rows={2}
                      className="form-control"
                      placeholder="ระบุผลงานหรือเงื่อนไขที่ได้รับคะแนนระดับ 5 (รอบ 12 เดือน)"
                      value={formData.Criteria12M_Level5}
                      onChange={handleInputChange}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* สูตรและตัวอย่าง */}
          <h5 className="font-weight-bold text-info border-bottom pb-2 mt-4">
            <i className="fas fa-calculator mr-2"></i> สูตรคำนวณและข้อมูลอ้างอิง
          </h5>

          <div className="form-group">
            <label className="font-weight-bold">
              สูตรคำนวณ (Calculation Formula)
            </label>
            <textarea
              name="CalculationFormula"
              rows={2}
              className="form-control"
              placeholder="เช่น (ผลงานจริง / ค่าเป้าหมาย) x 100"
              value={formData.CalculationFormula}
              onChange={handleInputChange}
            />
          </div>

          <div className="form-group">
            <label className="font-weight-bold">
              ตัวอย่างการคิดคะแนน (Score Example)
            </label>
            <textarea
              name="ScoreExample"
              rows={2}
              className="form-control"
              placeholder="เช่น หากทำได้ 85% จะตกอยู่ในเกณฑ์ระดับ 3 คิดเป็นคะแนน..."
              value={formData.ScoreExample}
              onChange={handleInputChange}
            />
          </div>

          <div className="form-group">
            <label className="font-weight-bold">
              ข้อมูลพื้นฐานปีก่อนหน้า (Baseline Data)
            </label>
            <textarea
              name="BaselineData"
              rows={2}
              className="form-control"
              placeholder="เช่น ผลการดำเนินงานปีงบประมาณ 2568 ได้ร้อยละ 80.5"
              value={formData.BaselineData}
              onChange={handleInputChange}
            />
          </div>

          <div className="text-right border-top pt-3 mt-3">
            <Button
              color="transparent"
              onClick={() => setIsFormModalOpen(false)}
              disabled={isSaving}
            >
              ยกเลิก
            </Button>
            <Button color="info" type="submit" disabled={isSaving}>
              {isSaving ? (
                <i className="fas fa-spinner fa-spin mr-1"></i>
              ) : (
                <i className="fas fa-save mr-1"></i>
              )}
              {editingItemId ? "บันทึกการแก้ไข" : "สร้างตัวชี้วัด"}
            </Button>
          </div>
        </form>
      </Modal>

      {/* ========================================================================= */}
      {/* --- Modal ดูรายละเอียดตัวชี้วัด (Detail Modal) --- */}
      {/* ========================================================================= */}
      <Modal
        isOpen={isDetailModalOpen}
        onRequestClose={() => setIsDetailModalOpen(false)}
        style={{
          overlay: styles.modalOverlay,
          content: styles.modalContent
        }}
        contentLabel="รายละเอียดตัวชี้วัด"
      >
        {viewingItem && (
          <div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                borderBottom: "2px solid #17a2b8",
                paddingBottom: "10px"
              }}
            >
              <h4 style={{ margin: 0, color: "#17a2b8", fontWeight: "bold" }}>
                <i className="fas fa-info-circle mr-2"></i> รายละเอียดตัวชี้วัด:{" "}
                {viewingItem.code}
              </h4>
              <button
                type="button"
                className="close"
                onClick={() => setIsDetailModalOpen(false)}
              >
                <span>&times;</span>
              </button>
            </div>

            <div className="mt-3">
              <h5 className="font-weight-bold text-dark">
                {viewingItem.kpiName}
              </h5>
              <div
                className="mb-2 d-flex flex-wrap align-items-center"
                style={{ gap: "6px" }}
              >
                <span
                  className={`badge ${
                    viewingItem.kpiType === 1 ? "badge-primary" : "badge-info"
                  }`}
                >
                  {viewingItem.kpiType === 1
                    ? "ตัวชี้วัดส่วนกลาง (Central KPI)"
                    : "ตัวชี้วัดระดับหน่วยงาน (Department KPI)"}
                </span>
                {viewingItem.status === 2 && (
                  <span className="badge badge-success">
                    <i className="fas fa-check-circle mr-1"></i> อนุมัติแล้ว
                    (Approved)
                  </span>
                )}
                {viewingItem.status === 1 && (
                  <span className="badge badge-warning text-dark">
                    <i className="fas fa-clock mr-1"></i> รอพิจารณา/รออนุมัติ
                    (Pending)
                  </span>
                )}
                {viewingItem.status === 3 && (
                  <span className="badge badge-danger">
                    <i className="fas fa-undo-alt mr-1"></i> ส่งกลับแก้ไข
                    (Rejected)
                  </span>
                )}
                {viewingItem.status === 0 && (
                  <span className="badge badge-secondary">
                    <i className="fas fa-file mr-1"></i> ฉบับร่าง (Draft)
                  </span>
                )}
                {viewingItem.weightType === 1 ? (
                  <span
                    className="badge badge-secondary"
                    title="ค่าน้ำหนักเปลี่ยนไม่ได้ ทุกคนใช้เหมือนกัน"
                  >
                    <i className="fas fa-lock mr-1"></i> ค่าน้ำหนัก: {viewingItem.defaultWeight}% (คงที่ เปลี่ยนไม่ได้)
                  </span>
                ) : (
                  <span
                    className="badge badge-success"
                    title="ค่าน้ำหนักสูงสุดที่ใช้ได้ ห้ามเกินนั้น"
                  >
                    <i className="fas fa-sliders-h mr-1"></i> ค่าน้ำหนักสูงสุด: {viewingItem.defaultWeight}% (ปรับได้ ห้ามเกินค่านี้)
                  </span>
                )}
                <span className="badge badge-info">
                  เป้าหมาย: ระดับ {viewingItem.targetScoreLevel}
                </span>
                <span className="badge badge-secondary">
                  หน่วย: {viewingItem.unitName}
                </span>
              </div>

              {/* ข้อมูลสายงาน ผู้จัดทำ ผู้ตรวจ และผู้อนุมัติ */}
              <div
                className="card bg-light p-2 mb-3"
                style={{ fontSize: "12.5px" }}
              >
                <div className="row">
                  <div className="col-md-6 mb-1">
                    <strong>
                      <i className="fas fa-building text-primary mr-1"></i>{" "}
                      สังกัดหน่วยงาน:
                    </strong>{" "}
                    {viewingItem.departmentName || "ส่วนกลาง / ทุกหน่วยงาน"}
                  </div>
                  <div className="col-md-6 mb-1">
                    <strong>
                      <i className="fas fa-user-edit text-info mr-1"></i>{" "}
                      ผู้จัดทำ:
                    </strong>{" "}
                    {viewingItem.createdFullName ||
                      viewingItem.createdBy ||
                      "-"}
                  </div>
                  <div className="col-md-6 mb-1">
                    <strong>
                      <i className="fas fa-user-check text-warning mr-1"></i>{" "}
                      ผู้ตรวจสอบ:
                    </strong>{" "}
                    {viewingItem.reviewerFullName ||
                      viewingItem.reviewerUserName ||
                      "หัวหน้าตามสายงาน"}
                  </div>
                  <div className="col-md-6 mb-1">
                    <strong>
                      <i className="fas fa-stamp text-success mr-1"></i>{" "}
                      ผู้อนุมัติ:
                    </strong>{" "}
                    {viewingItem.approvedFullName || viewingItem.approvedBy
                      ? `${viewingItem.approvedFullName ||
                          viewingItem.approvedBy} (${
                          viewingItem.approvedDate
                            ? new Date(
                                viewingItem.approvedDate
                              ).toLocaleDateString("th-TH")
                            : ""
                        })`
                      : "-"}
                  </div>
                </div>
              </div>

              {/* ข้อเสนอแนะกรณีส่งกลับแก้ไข */}
              {viewingItem.status === 3 && viewingItem.reviewRemark && (
                <div
                  className="alert alert-danger p-2 mb-3"
                  style={{ fontSize: "13px" }}
                >
                  <strong>
                    <i className="fas fa-exclamation-triangle mr-1"></i>{" "}
                    ข้อเสนอแนะที่ต้องปรับปรุงแก้ไข:
                  </strong>
                  <div className="mt-1 pl-3" style={{ whiteSpace: "pre-line" }}>
                    {viewingItem.reviewRemark}
                  </div>
                </div>
              )}

              {viewingItem.description && (
                <p className="text-muted" style={{ fontSize: "14px" }}>
                  {viewingItem.description}
                </p>
              )}

              {/* เกณฑ์ 5 ระดับ Cards (แยก 2 คอลัมน์ รอบ 6 เดือน และ 12 เดือน) */}
              <div className="mt-3 mb-2">
                <h6 className="font-weight-bold">
                  <i className="fas fa-list-ol mr-1"></i> เกณฑ์การให้คะแนน 5
                  ระดับ (Evaluation Criteria):
                </h6>
              </div>

              <div className="row">
                {/* คอลัมน์ซ้าย: รอบ 6 เดือน */}
                <div className="col-12 col-md-6 mb-3">
                  <div className="p-2 mb-2 rounded border border-primary bg-light d-flex justify-content-between align-items-center">
                    <span className="font-weight-bold text-primary">
                      <i className="far fa-calendar-alt mr-1"></i> เกณฑ์รอบ 6
                      เดือน
                    </span>
                    <span className="badge badge-primary">รอบที่ 1</span>
                  </div>

                  <div
                    style={{
                      ...styles.criteriaCard,
                      borderColor: "#dc3545",
                      backgroundColor: "#fff5f5"
                    }}
                  >
                    <strong className="text-danger">ระดับ 1:</strong>
                    <p
                      className="mb-0 mt-1"
                      style={{ whiteSpace: "pre-line", fontSize: "13px" }}
                    >
                      {viewingItem.criteriaLevel1 || "ไม่ได้ระบุ"}
                    </p>
                  </div>

                  <div
                    style={{
                      ...styles.criteriaCard,
                      borderColor: "#ffc107",
                      backgroundColor: "#fffdf0"
                    }}
                  >
                    <strong className="text-warning">ระดับ 2:</strong>
                    <p
                      className="mb-0 mt-1"
                      style={{ whiteSpace: "pre-line", fontSize: "13px" }}
                    >
                      {viewingItem.criteriaLevel2 || "ไม่ได้ระบุ"}
                    </p>
                  </div>

                  <div
                    style={{
                      ...styles.criteriaCard,
                      borderColor: "#007bff",
                      backgroundColor: "#f0f8ff"
                    }}
                  >
                    <strong className="text-primary">
                      ระดับ 3 (เป้าหมาย 6 เดือน):
                    </strong>
                    <p
                      className="mb-0 mt-1"
                      style={{ whiteSpace: "pre-line", fontSize: "13px" }}
                    >
                      {viewingItem.criteriaLevel3 || "ไม่ได้ระบุ"}
                    </p>
                  </div>

                  <div
                    style={{
                      ...styles.criteriaCard,
                      borderColor: "#17a2b8",
                      backgroundColor: "#f0fbff"
                    }}
                  >
                    <strong className="text-info">ระดับ 4:</strong>
                    <p
                      className="mb-0 mt-1"
                      style={{ whiteSpace: "pre-line", fontSize: "13px" }}
                    >
                      {viewingItem.criteriaLevel4 || "ไม่ได้ระบุ"}
                    </p>
                  </div>

                  <div
                    style={{
                      ...styles.criteriaCard,
                      borderColor: "#28a745",
                      backgroundColor: "#f0fff4"
                    }}
                  >
                    <strong className="text-success">
                      ระดับ 5 (ดีเยี่ยม):
                    </strong>
                    <p
                      className="mb-0 mt-1"
                      style={{ whiteSpace: "pre-line", fontSize: "13px" }}
                    >
                      {viewingItem.criteriaLevel5 || "ไม่ได้ระบุ"}
                    </p>
                  </div>
                </div>

                {/* คอลัมน์ขวา: รอบ 12 เดือน */}
                <div className="col-12 col-md-6 mb-3">
                  <div className="p-2 mb-2 rounded border border-success bg-light d-flex justify-content-between align-items-center">
                    <span className="font-weight-bold text-success">
                      <i className="far fa-calendar-check mr-1"></i> เกณฑ์รอบ 12
                      เดือน (สิ้นปี)
                    </span>
                    <span className="badge badge-success">รอบที่ 2</span>
                  </div>

                  <div
                    style={{
                      ...styles.criteriaCard,
                      borderColor: "#dc3545",
                      backgroundColor: "#fff5f5"
                    }}
                  >
                    <strong className="text-danger">ระดับ 1:</strong>
                    <p
                      className="mb-0 mt-1"
                      style={{ whiteSpace: "pre-line", fontSize: "13px" }}
                    >
                      {viewingItem.criteria12M_Level1 || (
                        <span className="text-muted font-italic">
                          {viewingItem.criteriaLevel1 || "ไม่ได้ระบุ"}
                        </span>
                      )}
                    </p>
                  </div>

                  <div
                    style={{
                      ...styles.criteriaCard,
                      borderColor: "#ffc107",
                      backgroundColor: "#fffdf0"
                    }}
                  >
                    <strong className="text-warning">ระดับ 2:</strong>
                    <p
                      className="mb-0 mt-1"
                      style={{ whiteSpace: "pre-line", fontSize: "13px" }}
                    >
                      {viewingItem.criteria12M_Level2 || (
                        <span className="text-muted font-italic">
                          {viewingItem.criteriaLevel2 || "ไม่ได้ระบุ"}
                        </span>
                      )}
                    </p>
                  </div>

                  <div
                    style={{
                      ...styles.criteriaCard,
                      borderColor: "#007bff",
                      backgroundColor: "#f0f8ff"
                    }}
                  >
                    <strong className="text-primary">
                      ระดับ 3 (เป้าหมาย 12 เดือน):
                    </strong>
                    <p
                      className="mb-0 mt-1"
                      style={{ whiteSpace: "pre-line", fontSize: "13px" }}
                    >
                      {viewingItem.criteria12M_Level3 || (
                        <span className="text-muted font-italic">
                          {viewingItem.criteriaLevel3 || "ไม่ได้ระบุ"}
                        </span>
                      )}
                    </p>
                  </div>

                  <div
                    style={{
                      ...styles.criteriaCard,
                      borderColor: "#17a2b8",
                      backgroundColor: "#f0fbff"
                    }}
                  >
                    <strong className="text-info">ระดับ 4:</strong>
                    <p
                      className="mb-0 mt-1"
                      style={{ whiteSpace: "pre-line", fontSize: "13px" }}
                    >
                      {viewingItem.criteria12M_Level4 || (
                        <span className="text-muted font-italic">
                          {viewingItem.criteriaLevel4 || "ไม่ได้ระบุ"}
                        </span>
                      )}
                    </p>
                  </div>

                  <div
                    style={{
                      ...styles.criteriaCard,
                      borderColor: "#28a745",
                      backgroundColor: "#f0fff4"
                    }}
                  >
                    <strong className="text-success">
                      ระดับ 5 (ดีเยี่ยม):
                    </strong>
                    <p
                      className="mb-0 mt-1"
                      style={{ whiteSpace: "pre-line", fontSize: "13px" }}
                    >
                      {viewingItem.criteria12M_Level5 || (
                        <span className="text-muted font-italic">
                          {viewingItem.criteriaLevel5 || "ไม่ได้ระบุ"}
                        </span>
                      )}
                    </p>
                  </div>
                </div>
              </div>

              {/* สูตรและตัวอย่าง */}
              <div
                className="card bg-light mt-3 p-3"
                style={{ fontSize: "13px" }}
              >
                <div className="mb-2">
                  <strong>
                    <i className="fas fa-calculator mr-1"></i> สูตรคำนวณ:
                  </strong>
                  <div className="text-secondary pl-3">
                    {viewingItem.calculationFormula || "-"}
                  </div>
                </div>
                <div className="mb-2">
                  <strong>
                    <i className="fas fa-lightbulb mr-1"></i>{" "}
                    ตัวอย่างการคิดคะแนน:
                  </strong>
                  <div className="text-secondary pl-3">
                    {viewingItem.scoreExample || "-"}
                  </div>
                </div>
                <div>
                  <strong>
                    <i className="fas fa-database mr-1"></i> ข้อมูลพื้นฐาน
                    (Baseline):
                  </strong>
                  <div className="text-secondary pl-3">
                    {viewingItem.baselineData || "-"}
                  </div>
                </div>
              </div>

              {/* รายชื่อผู้รับมอบหมาย */}
              <div className="card border-info mt-3 p-3">
                <strong className="text-info mb-2">
                  <i className="fas fa-users mr-1"></i>{" "}
                  ผู้รับมอบหมายตัวชี้วัดนี้ (
                  {
                    (
                      viewingItem.assignedUsers ||
                      viewingItem.userAssignments ||
                      []
                    ).length
                  }{" "}
                  คน):
                </strong>
                {!(viewingItem.assignedUsers || viewingItem.userAssignments) ||
                (viewingItem.assignedUsers || viewingItem.userAssignments)
                  .length === 0 ? (
                  <span className="text-muted" style={{ fontSize: "13px" }}>
                    ยังไม่มีผู้รับมอบหมาย
                  </span>
                ) : (
                  <div className="d-flex flex-wrap" style={{ gap: "6px" }}>
                    {(
                      viewingItem.assignedUsers || viewingItem.userAssignments
                    ).map(u => (
                      <span
                        key={u.assignmentID || u.userName}
                        className="badge badge-light border px-2 py-1"
                        style={{ fontSize: "12px" }}
                      >
                        <i className="fas fa-user mr-1 text-primary"></i>
                        {u.fullName || u.userName} ({u.weight}%)
                      </span>
                    ))}
                  </div>
                )}
              </div>

              <div className="d-flex justify-content-between align-items-center border-top pt-3 mt-3">
                <div>
                  {viewingItem.canApprove && (
                    <div className="d-flex" style={{ gap: "8px" }}>
                      {viewingItem.status !== 2 && (
                        <Button
                          color="success"
                          size="sm"
                          onClick={() => {
                            setIsDetailModalOpen(false);
                            handleQuickApprove(viewingItem);
                          }}
                        >
                          <i className="fas fa-check mr-1"></i> อนุมัติตัวชี้วัด
                        </Button>
                      )}
                      <Button
                        color="danger"
                        size="sm"
                        onClick={() => {
                          setIsDetailModalOpen(false);
                          handleOpenRejectModal(viewingItem);
                        }}
                      >
                        <i className="fas fa-undo mr-1"></i> ส่งกลับแก้ไข
                      </Button>
                    </div>
                  )}
                </div>
                <Button
                  color="secondary"
                  onClick={() => setIsDetailModalOpen(false)}
                >
                  ปิดหน้าต่าง
                </Button>
              </div>
            </div>
          </div>
        )}
      </Modal>

      {/* ========================================================================= */}
      {/* --- Modal มอบหมายผู้ใช้ (Assign Users to KPI) ด้วย TSUser Component --- */}
      {/* ========================================================================= */}
      <Modal
        isOpen={isAssignModalOpen}
        onRequestClose={() => setIsAssignModalOpen(false)}
        style={{
          overlay: styles.modalOverlay,
          content: styles.assignModalContent
        }}
        contentLabel="มอบหมายตัวชี้วัดให้ผู้รับการประเมิน"
      >
        {assigningItem && (
          <div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                borderBottom: "2px solid #28a745",
                paddingBottom: "10px"
              }}
            >
              <h4 style={{ margin: 0, color: "#28a745", fontWeight: "bold" }}>
                <i className="fas fa-user-check mr-2"></i>{" "}
                มอบหมายผู้รับการประเมินตัวชี้วัด
              </h4>
              <button
                type="button"
                className="close"
                onClick={() => setIsAssignModalOpen(false)}
              >
                <span>&times;</span>
              </button>
            </div>

            <div className="mt-3">
              <div
                className="alert alert-info py-2"
                style={{ fontSize: "13px" }}
              >
                <strong>ตัวชี้วัด:</strong> {assigningItem.kpiName} (
                {assigningItem.weightType === 1 ? (
                  <>ค่าน้ำหนัก: {assigningItem.defaultWeight}% - เปลี่ยนไม่ได้ ทุกคนใช้เหมือนกัน</>
                ) : (
                  <>ค่าน้ำหนักสูงสุด: {assigningItem.defaultWeight}% - ค่าน้ำหนักสูงสุดที่ใช้ได้ ห้ามเกินนั้น</>
                )}
                )
              </div>

              {/* Component TSUser สำหรับเลือกเจ้าหน้าที่ตามกลุ่มงาน/บุคคล */}
              <div className="mb-3">
                <TSUser
                  value={assignedUsersList}
                  isMultiple={true}
                  onChange={handleUserChange}
                />
              </div>

              {/* ตารางรายชื่อที่ถูกมอบหมาย และการกำหนด % น้ำหนัก */}
              <div className="card border-light shadow-sm mt-3">
                <div className="card-header bg-light d-flex justify-content-between align-items-center py-2">
                  <span
                    className="font-weight-bold"
                    style={{ fontSize: "14px" }}
                  >
                    <i className="fas fa-users-cog mr-2 text-primary"></i>
                    กำหนด % น้ำหนักรายบุคคล ({assignedUsersList.length} คน)
                  </span>
                  <span
                    className={`badge ${
                      assigningItem.weightType === 1
                        ? "badge-danger text-white"
                        : "badge-info"
                    }`}
                    style={{ fontSize: "12px" }}
                  >
                    {assigningItem.weightType === 1 ? (
                      <>
                        <i className="fas fa-lock mr-1"></i>น้ำหนักบังคับ (เปลี่ยนไม่ได้ ทุกคนใช้ {assigningItem.defaultWeight}%)
                      </>
                    ) : (
                      <>
                        <i className="fas fa-sliders-h mr-1"></i>น้ำหนักสูงสุด: {assigningItem.defaultWeight}% (ปรับรายคนได้ ห้ามเกิน {assigningItem.defaultWeight}%)
                      </>
                    )}
                  </span>
                </div>
                <div className="card-body p-0">
                  <div
                    className="table-responsive"
                    style={{ maxHeight: "250px", overflowY: "auto" }}
                  >
                    <table className="table table-sm table-bordered table-hover mb-0">
                      <thead className="thead-light">
                        <tr
                          style={{
                            backgroundColor: "#f8f9fa",
                            textAlign: "center"
                          }}
                        >
                          <th style={{ width: "50px" }}>#</th>
                          <th>ชื่อ - สกุล / ชื่อเล่น</th>
                          <th style={{ width: "160px" }}>
                            ชื่อผู้ใช้ (UserName)
                          </th>
                          <th style={{ width: "160px" }}>
                            {assigningItem.weightType === 1
                              ? "% น้ำหนัก (คงที่)"
                              : `% น้ำหนัก (สูงสุด ${assigningItem.defaultWeight}%)`}
                          </th>
                          <th style={{ width: "70px" }}>ลบ</th>
                        </tr>
                      </thead>
                      <tbody>
                        {assignedUsersList.length === 0 ? (
                          <tr>
                            <td
                              colSpan="5"
                              className="text-center text-muted py-4"
                            >
                              <i className="fas fa-info-circle mr-1"></i>{" "}
                              ยังไม่มีผู้รับมอบหมาย
                              กรุณาเลือกเจ้าหน้าที่จากผังกลุ่มงานด้านบน
                            </td>
                          </tr>
                        ) : (
                          assignedUsersList.map((u, idx) => (
                            <tr key={u.userName}>
                              <td className="text-center align-middle">
                                {idx + 1}
                              </td>
                              <td className="align-middle">
                                <strong>
                                  {u.fullName ||
                                    `${u.firstName || ""} ${u.lastName ||
                                      ""}` ||
                                    u.userName}
                                </strong>
                                {u.position && (
                                  <div
                                    className="text-muted"
                                    style={{ fontSize: "11px" }}
                                  >
                                    {u.position}
                                  </div>
                                )}
                              </td>
                              <td
                                className="align-middle text-muted"
                                style={{ fontSize: "12px" }}
                              >
                                <i className="fas fa-user mr-1 text-secondary"></i>
                                {u.userName}
                              </td>
                              <td className="text-center align-middle">
                                <div
                                  className="input-group input-group-sm"
                                  style={{ width: "110px", margin: "0 auto" }}
                                >
                                  <input
                                    type="number"
                                    step="0.1"
                                    min="0"
                                    max={
                                      assigningItem.weightType === 1
                                        ? assigningItem.defaultWeight
                                        : assigningItem.defaultWeight
                                    }
                                    disabled={assigningItem.weightType === 1}
                                    className={`form-control text-center font-weight-bold ${
                                      assigningItem.weightType === 1
                                        ? "text-muted bg-light"
                                        : (parseFloat(u.weight) || 0) >
                                          (assigningItem.defaultWeight || 0)
                                        ? "border-danger text-danger bg-warning-light"
                                        : "text-primary"
                                    }`}
                                    value={
                                      assigningItem.weightType === 1
                                        ? assigningItem.defaultWeight
                                        : u.weight
                                    }
                                    onChange={e =>
                                      handleUserWeightChange(
                                        u.userName,
                                        e.target.value
                                      )
                                    }
                                    onBlur={e => {
                                      if (assigningItem.weightType === 2) {
                                        const maxWt =
                                          assigningItem.defaultWeight || 0;
                                        const entered =
                                          parseFloat(e.target.value) || 0;
                                        if (entered > maxWt) {
                                          alert(
                                            `ค่าน้ำหนักต้องไม่เกินค่าน้ำหนักสูงสุด (${maxWt}%)`
                                          );
                                          handleUserWeightChange(
                                            u.userName,
                                            maxWt
                                          );
                                        }
                                      }
                                    }}
                                    title={
                                      assigningItem.weightType === 1
                                        ? "ตัวชี้วัดนี้น้ำหนักเป็นแบบบังคับ เปลี่ยนไม่ได้ ใช้เหมือนกันทุกคน"
                                        : `ปรับน้ำหนักสำหรับบุคคลนี้ (ค่าน้ำหนักสูงสุด ${assigningItem.defaultWeight}% ห้ามเกินค่านี้)`
                                    }
                                  />
                                  <div className="input-group-append">
                                    <span className="input-group-text">%</span>
                                  </div>
                                </div>
                                {assigningItem.weightType === 2 &&
                                  (parseFloat(u.weight) || 0) >
                                    (assigningItem.defaultWeight || 0) && (
                                    <div
                                      className="text-danger font-weight-bold"
                                      style={{
                                        fontSize: "10px",
                                        marginTop: "2px"
                                      }}
                                    >
                                      เกินค่าสูงสุด ({assigningItem.defaultWeight}%)
                                    </div>
                                  )}
                              </td>
                              <td className="text-center align-middle">
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-danger"
                                  style={{ padding: "2px 6px" }}
                                  onClick={() =>
                                    handleRemoveUserFromAssign(u.userName)
                                  }
                                  title="นำออกจากรายการ"
                                >
                                  <i className="fas fa-trash-alt"></i>
                                </button>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              <div className="text-right border-top pt-3 mt-3">
                <Button
                  color="transparent"
                  onClick={() => setIsAssignModalOpen(false)}
                  disabled={isSaving}
                >
                  ยกเลิก
                </Button>
                <Button
                  color="success"
                  onClick={handleSaveAssignments}
                  disabled={isSaving}
                >
                  {isSaving ? (
                    <i className="fas fa-spinner fa-spin mr-1"></i>
                  ) : (
                    <i className="fas fa-check mr-1"></i>
                  )}
                  บันทึกการมอบหมาย
                </Button>
              </div>
            </div>
          </div>
        )}
      </Modal>

      {/* ========================================================================= */}
      {/* Modal 4: จัดการตัวเลือกแผนงาน / โครงการ (Manage Plan/Project Options) */}
      {/* ========================================================================= */}
      <Modal
        isOpen={isProjectModalOpen}
        onRequestClose={() => setIsProjectModalOpen(false)}
        style={{
          overlay: styles.modalOverlay,
          content: {
            ...styles.modalContent,
            width: "550px",
            maxWidth: "94vw"
          }
        }}
        contentLabel="จัดการตัวเลือกแผนงาน/โครงการ"
      >
        <div>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              borderBottom: "2px solid #007bff",
              paddingBottom: "10px"
            }}
          >
            <h4 style={{ margin: 0, color: "#007bff", fontWeight: "bold" }}>
              <i className="fas fa-tasks mr-2"></i> จัดการตัวเลือกแผนงาน /
              โครงการ
            </h4>
            <button
              type="button"
              className="close"
              onClick={() => setIsProjectModalOpen(false)}
            >
              <span>&times;</span>
            </button>
          </div>

          <div className="mt-3">
            {/* ฟอร์มเพิ่มตัวเลือกใหม่ */}
            <form onSubmit={handleAddCustomProject} className="mb-3">
              <label
                className="font-weight-bold text-dark"
                style={{ fontSize: "13px" }}
              >
                + เพิ่มแผนงาน / โครงการใหม่
              </label>
              <div className="input-group">
                <input
                  type="text"
                  className="form-control"
                  placeholder="ระบุชื่อแผนงานหรือโครงการใหม่..."
                  value={newProjectName}
                  onChange={e => setNewProjectName(e.target.value)}
                />
                <div className="input-group-append">
                  <Button color="primary" type="submit" style={{ margin: 0 }}>
                    <i className="fas fa-plus mr-1"></i> เพิ่ม
                  </Button>
                </div>
              </div>
            </form>

            {/* รายการตัวเลือกที่มี */}
            <div className="d-flex justify-content-between align-items-center mb-1">
              <label
                className="font-weight-bold text-muted mb-0"
                style={{ fontSize: "13px" }}
              >
                รายการตัวเลือกทั้งหมดในระบบ ({allPlanProjects.length} รายการ)
              </label>
              <span className="text-muted" style={{ fontSize: "11px" }}>
                * 3 รายการแรกเป็นค่าหลักของระบบ
              </span>
            </div>

            <div
              className="list-group"
              style={{ maxHeight: "300px", overflowY: "auto" }}
            >
              {allPlanProjects.map(proj => {
                const isDefault = DEFAULT_PLAN_PROJECTS.includes(proj);
                const isSelected = formData.PlanProjectName === proj;

                return (
                  <div
                    key={proj}
                    className={`list-group-item d-flex justify-content-between align-items-center py-2 px-3 ${
                      isSelected ? "list-group-item-primary" : ""
                    }`}
                  >
                    <div
                      className="d-flex align-items-center"
                      style={{ flex: 1, minWidth: 0, marginRight: "8px" }}
                    >
                      <i
                        className={`fas ${
                          isDefault
                            ? "fa-cube text-primary"
                            : "fa-folder-open text-info"
                        } mr-2`}
                      ></i>
                      <span
                        className={`${
                          isDefault ? "font-weight-bold" : ""
                        } text-truncate`}
                      >
                        {proj}
                      </span>
                      {isDefault && (
                        <span
                          className="badge badge-secondary ml-2"
                          style={{ fontSize: "10px", flexShrink: 0 }}
                        >
                          ค่าหลัก
                        </span>
                      )}
                      {isSelected && (
                        <span
                          className="badge badge-success ml-2"
                          style={{ fontSize: "10px", flexShrink: 0 }}
                        >
                          กำลังเลือก
                        </span>
                      )}
                    </div>

                    <div style={{ flexShrink: 0 }}>
                      {!isSelected && (
                        <button
                          type="button"
                          className="btn btn-xs btn-outline-success mr-1 py-1 px-2"
                          style={{ fontSize: "11px" }}
                          onClick={() => {
                            setFormData(prev => ({
                              ...prev,
                              PlanProjectName: proj
                            }));
                            setIsProjectModalOpen(false);
                          }}
                          title="เลือกรายการนี้"
                        >
                          <i className="fas fa-check mr-1"></i> เลือก
                        </button>
                      )}
                      {!isDefault && (
                        <button
                          type="button"
                          className="btn btn-xs btn-outline-danger py-1 px-2"
                          style={{ fontSize: "11px" }}
                          onClick={() => handleDeleteCustomProject(proj)}
                          title="ลบตัวเลือกนี้"
                        >
                          <i className="fas fa-trash-alt"></i>
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="text-right border-top pt-3 mt-3">
              <Button
                color="secondary"
                onClick={() => setIsProjectModalOpen(false)}
              >
                ปิดหน้าต่าง
              </Button>
            </div>
          </div>
        </div>
      </Modal>

      {/* ========================================================================= */}
      {/* Modal 5: ส่งกลับแก้ไขตัวชี้วัด (Reject / Return for Revision) */}
      {/* ========================================================================= */}
      <Modal
        isOpen={isReviewModalOpen}
        onRequestClose={() => {
          setIsReviewModalOpen(false);
          setReviewingItem(null);
        }}
        style={{
          overlay: styles.modalOverlay,
          content: {
            ...styles.modalContent,
            width: "550px",
            maxWidth: "94vw"
          }
        }}
        contentLabel="ส่งกลับแก้ไขตัวชี้วัด"
      >
        <div>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              borderBottom: "2px solid #dc3545",
              paddingBottom: "10px"
            }}
          >
            <h4 style={{ margin: 0, color: "#dc3545", fontWeight: "bold" }}>
              <i className="fas fa-undo-alt mr-2"></i>{" "}
              ส่งกลับให้ผู้จัดทำแก้ไขตัวชี้วัด
            </h4>
            <button
              type="button"
              className="close"
              onClick={() => {
                setIsReviewModalOpen(false);
                setReviewingItem(null);
              }}
            >
              <span>&times;</span>
            </button>
          </div>

          <div className="mt-3">
            {reviewingItem && (
              <div
                className="alert alert-light border mb-3"
                style={{ fontSize: "13px" }}
              >
                <strong>ตัวชี้วัด:</strong> {reviewingItem.kpiName}
                <br />
                <span className="text-muted">
                  ผู้จัดทำ:{" "}
                  {reviewingItem.createdFullName ||
                    reviewingItem.createdBy ||
                    "-"}
                </span>
              </div>
            )}

            <div className="form-group">
              <label className="font-weight-bold text-dark">
                ข้อเสนอแนะ / รายละเอียดที่ต้องปรับปรุงแก้ไข{" "}
                <span className="text-danger">*</span>
              </label>
              <textarea
                rows={4}
                className="form-control"
                placeholder="ระบุข้อความแจ้งผู้จัดทำ เช่น ให้ปรับเกณฑ์ระดับ 3 ให้สอดคล้องกับเป้าหมายประจำปี หรือแก้ไขค่าน้ำหนัก..."
                value={reviewRemark}
                onChange={e => setReviewRemark(e.target.value)}
                required
              />
            </div>

            <div
              className="d-flex justify-content-end border-top pt-3 mt-3"
              style={{ gap: "8px" }}
            >
              <Button
                color="secondary"
                onClick={() => {
                  setIsReviewModalOpen(false);
                  setReviewingItem(null);
                }}
                disabled={isSaving}
              >
                ยกเลิก
              </Button>
              <Button
                color="danger"
                onClick={handleSubmitReview}
                disabled={isSaving || !reviewRemark.trim()}
              >
                {isSaving ? (
                  <i className="fas fa-spinner fa-spin mr-1"></i>
                ) : (
                  <i className="fas fa-paper-plane mr-1"></i>
                )}
                ยืนยันการส่งกลับแก้ไข
              </Button>
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default KpiManagement;
